import React from 'react';
import classNames from 'classnames';
import { NamedLink } from '../../components';
import { useIntl } from '../../util/reactIntl';

import css from './TabNav.module.css';

const TabContent = ({ selected, icon, text, badge, badgeComplete, completeLabel }) => (
  <>
    {selected ? <span className={css.accentBar} aria-hidden="true" /> : null}
    {icon ? <span className={css.icon}>{icon}</span> : null}
    <span className={css.label}>{text}</span>
    {badge != null ? (
      <>
        <span
          className={classNames(css.badge, { [css.badgeComplete]: badgeComplete })}
          aria-hidden="true"
        >
          {badge}
        </span>
        {badgeComplete && completeLabel ? (
          <span className={css.visuallyHidden}>{completeLabel}</span>
        ) : null}
      </>
    ) : null}
  </>
);

const Tab = props => {
  const {
    className,
    id,
    disabled,
    text,
    selected,
    linkProps,
    onClick,
    icon,
    badge,
    badgeComplete,
    completeLabel,
  } = props;

  const linkClasses = classNames(css.link, {
    [css.selectedLink]: selected,
    [css.disabled]: disabled,
  });

  return (
    <div id={id} className={className}>
      {disabled ? (
        <span className={linkClasses} aria-disabled="true">
          <TabContent
            selected={selected}
            icon={icon}
            text={text}
            badge={badge}
            badgeComplete={badgeComplete}
            completeLabel={completeLabel}
          />
        </span>
      ) : typeof onClick === 'function' ? (
        <button
          type="button"
          className={linkClasses}
          aria-current={selected ? 'true' : undefined}
          onClick={onClick}
        >
          <TabContent
            selected={selected}
            icon={icon}
            text={text}
            badge={badge}
            badgeComplete={badgeComplete}
            completeLabel={completeLabel}
          />
        </button>
      ) : (
        <NamedLink className={linkClasses} {...linkProps}>
          <TabContent
            selected={selected}
            icon={icon}
            text={text}
            badge={badge}
            badgeComplete={badgeComplete}
            completeLabel={completeLabel}
          />
        </NamedLink>
      )}
    </div>
  );
};

/**
 * @typedef {Object} TabConfig
 * @property {string|React.ReactNode} text - The text to be rendered in the tab
 * @property {React.ReactNode} [icon] - Optional outline icon rendered before the label
 * @property {boolean} disabled - Whether the tab is disabled
 * @property {boolean} selected - Whether the tab is selected
 * @property {Object} linkProps - The props to be passed to the link component
 * @property {string} linkProps.name - The name of the link
 * @property {Object} [linkProps.params] - The path params to be passed to the link component
 * @property {Object} [linkProps.to] - The rest of the URL params needed
 */

/**
 * Vertical / horizontal tab navigation (Style B).
 * Desktop: stacked pills. Mobile: horizontal scroll chips.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.tabRootClassName] - Custom class that overrides the default class for the tab element
 * @param {Array<TabConfig>} props.tabs - The tabs to render
 * @param {string} [props.ariaLabel] - Accessible name for the nav
 * @returns {JSX.Element}
 */
const TabNav = props => {
  const { className, rootClassName, tabRootClassName, tabs, ariaLabel } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);
  const tabClasses = tabRootClassName || css.tab;
  const completeLabel = intl.formatMessage({ id: 'TabNav.complete' });
  return (
    <nav className={classes} aria-label={ariaLabel}>
      {tabs.map((tab, index) => {
        const id = typeof tab.id === 'string' ? tab.id : `${index}`;
        return (
          <Tab key={id} id={id} className={tabClasses} completeLabel={completeLabel} {...tab} />
        );
      })}
    </nav>
  );
};

export default TabNav;
